import { getBezierLabelGeometry } from "../utils/bezierLabelGeometry.js";
import {
  getAdaptiveEdgeGeometry,
  getEdgeTopology,
  getFloatingEdgeEndpoints,
  getNodeRect,
} from "../utils/customEdgeGeometry.js";

// React Flow fits node boxes, not their painted shadows. A four-percent floor
// preserves the node elevation and edge decorations while matching the visual
// inset used by frameless map titles.
const MIN_PADDING = 0.04;
const MAX_PADDING = 0.1;
const VISUAL_SAFETY_MARGIN = 18;

export function calculateEmbedZoomScale(widthMode = "normal", width = 0) {
  // Mobile width modes collapse toward the viewport edge, so extra zoom there
  // would only clip the graph. On larger canvases, make the graph itself—not
  // merely its transparent host—visibly larger for wide and full embeds.
  if (width <= 640) return 1;
  if (widthMode === "full") return 1.32;
  if (widthMode === "wide") return 1.16;
  return 1;
}

function targetGutter(width, showFrame, showBackground, widthMode) {
  const seamless = !showFrame && !showBackground;
  const partiallySeamless = !showFrame || !showBackground;
  let gutter;

  if (width <= 640) {
    gutter = seamless ? 10 : partiallySeamless ? 14 : 18;
  } else if (width <= 900) {
    gutter = seamless ? 14 : partiallySeamless ? 18 : 22;
  } else {
    gutter = seamless ? 18 : partiallySeamless ? 22 : 28;
  }

  if (widthMode === "wide") gutter -= 2;
  if (widthMode === "full") gutter -= 4;
  return Math.max(8, gutter);
}

export function calculateEmbedFitPadding({
  width,
  height,
  showFrame,
  showBackground,
  widthMode = "normal",
}) {
  const limitingDimension = Math.max(1, Math.min(width || 0, height || 0));
  const proportionalPadding =
    targetGutter(width || 0, showFrame, showBackground, widthMode) /
    limitingDimension;

  return Math.min(MAX_PADDING, Math.max(MIN_PADDING, proportionalPadding));
}

export function calculateAutomaticEmbedHeight({
  width,
  viewportHeight = 0,
  widthMode = "normal",
}) {
  if (!width) return null;

  let minimumHeight;
  if (width <= 480) {
    minimumHeight = 300;
  } else if (width <= 640) {
    minimumHeight = 320;
  } else if (width <= 900) {
    minimumHeight = 360;
  } else {
    minimumHeight = 420;
  }

  // Keep container sizing independent from graph measurements. Mixing node
  // bounds into host height creates a feedback loop: changing the host changes
  // the fit, which changes the measured layout, which changes the host again.
  // The original embed's stable 650px desktop canvas is the upper bound;
  // fitBounds is solely responsible for fitting graph content inside it.
  let desiredHeight;
  if (width <= 480) {
    desiredHeight = width * 0.9;
  } else if (width <= 640) {
    desiredHeight = width * 0.82;
  } else {
    desiredHeight = 650;
  }

  if (viewportHeight > 0) {
    const viewportReadingHeight = Math.round(viewportHeight * 0.82);
    desiredHeight = Math.min(desiredHeight, viewportReadingHeight);
  }

  const landscape = viewportHeight > 0 && width > viewportHeight * 1.2;
  if (landscape) {
    minimumHeight = Math.min(minimumHeight, 320);
  }

  const widthScale = calculateEmbedZoomScale(widthMode, width);
  return Math.round(Math.max(minimumHeight, desiredHeight) * widthScale);
}

export function calculateAspectFittedEmbedHeight({
  width,
  visualBounds,
  titleHeight = 0,
  minimumHeight = 0,
  maximumHeight = 1200,
}) {
  if (
    width <= 640 ||
    !visualBounds ||
    visualBounds.width <= 0 ||
    visualBounds.height <= 0
  ) {
    return Math.round(minimumHeight) || null;
  }

  // Matching the canvas aspect ratio to the graph aspect ratio lets fitView
  // use both dimensions at once. The graph then reaches the frame's left and
  // right fit gutters without sacrificing its top and bottom bounds.
  const graphHeight = width * (visualBounds.height / visualBounds.width);
  const desiredHeight = graphHeight + Math.max(0, titleHeight);
  return Math.round(Math.min(maximumHeight, Math.max(minimumHeight, desiredHeight)));
}

function includePoint(bounds, x, y, margin = 0) {
  bounds.minX = Math.min(bounds.minX, x - margin);
  bounds.minY = Math.min(bounds.minY, y - margin);
  bounds.maxX = Math.max(bounds.maxX, x + margin);
  bounds.maxY = Math.max(bounds.maxY, y + margin);
}

export function calculateMapVisualBounds(nodes, edges) {
  const bounds = {
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  };
  const nodesById = new Map();

  for (const node of nodes) {
    const rect = getNodeRect(node);
    if (!rect) return null;
    nodesById.set(String(node.id), rect);
    includePoint(bounds, rect.x, rect.y, VISUAL_SAFETY_MARGIN);
    includePoint(
      bounds,
      rect.x + rect.width,
      rect.y + rect.height,
      VISUAL_SAFETY_MARGIN
    );
  }

  for (const edge of edges) {
    const source = nodesById.get(String(edge.source));
    const target = nodesById.get(String(edge.target));
    if (!source || !target) continue;

    const endpoints = getFloatingEdgeEndpoints({
      sourceNode: source,
      targetNode: target,
      sourceId: edge.source,
      targetId: edge.target,
      reciprocal: Boolean(edge.data?.reciprocal),
    });
    if (!endpoints) continue;
    const obstacleRects = Array.from(nodesById.entries())
      .filter(([nodeId]) => nodeId !== String(edge.source) && nodeId !== String(edge.target))
      .map(([, rect]) => rect);
    const geometry = getAdaptiveEdgeGeometry({
      ...endpoints,
      reciprocal: Boolean(edge.data?.reciprocal),
      obstacleRects,
      seed: edge.id,
      routingMode: edge.data?.routing,
      topology: getEdgeTopology({
        edges,
        nodeCount: nodes.length,
        sourceId: edge.source,
        targetId: edge.target,
      }),
    });

    for (const point of geometry.points) {
      includePoint(bounds, point.x, point.y, VISUAL_SAFETY_MARGIN);
    }

    if (edge.label) {
      const label = getBezierLabelGeometry(geometry.points);
      const angle = (label.angle * Math.PI) / 180;
      const halfWidth = Math.min(140, String(edge.label).length * 3.6 + 4);
      const halfHeight = 10;
      const extentX =
        Math.abs(Math.cos(angle)) * halfWidth + Math.abs(Math.sin(angle)) * halfHeight;
      const extentY =
        Math.abs(Math.sin(angle)) * halfWidth + Math.abs(Math.cos(angle)) * halfHeight;
      includePoint(bounds, label.x, label.y);
      includePoint(bounds, label.x - extentX, label.y - extentY);
      includePoint(bounds, label.x + extentX, label.y + extentY);
    }
  }

  if (!Number.isFinite(bounds.minX)) {
    return null;
  }

  return {
    x: bounds.minX,
    y: bounds.minY,
    width: Math.max(1, bounds.maxX - bounds.minX),
    height: Math.max(1, bounds.maxY - bounds.minY),
  };
}
